import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AirplaneTilt, Star, CalendarBlank, Package as PackageIcon, User, ChatCircleDots } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const TripDetails = () => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [parcels, setParcels] = useState([]);
  const [selectedParcel, setSelectedParcel] = useState('');
  const [requestedPrice, setRequestedPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tripRes = await fetch(`${API}/trips/${tripId}`, { credentials: 'include' });
        const tripData = await tripRes.json();
        setTrip(tripData);

        const parcelsRes = await fetch(`${API}/parcels/my`, { credentials: 'include' });
        const parcelsData = await parcelsRes.json();
        setParcels(parcelsData.filter(p => p.status === 'pending'));

        setLoading(false);
      } catch (error) {
        toast.error('Failed to load trip details');
        setLoading(false);
      }
    };

    fetchData();
  }, [tripId]);

  const handleBookingRequest = async () => {
    if (!selectedParcel || !requestedPrice) {
      toast.error('Please select a parcel and enter a price');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          parcel_id: selectedParcel,
          trip_id: tripId,
          requested_price: parseFloat(requestedPrice)
        })
      });

      if (!response.ok) throw new Error('Failed to create booking');

      toast.success('Booking request sent successfully!');
      setDialogOpen(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to send booking request');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D28D9]"></div>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Trip not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="flex items-start justify-between mb-8">
            <div className="flex items-center gap-4">
              {trip.traveler_image ? (
                <img
                  src={trip.traveler_image}
                  alt={trip.traveler_name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#1E2A45] flex items-center justify-center">
                  <User size={32} weight="duotone" className="text-gray-600" />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {trip.traveler_name}
                </h2>
                {trip.traveler_rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star size={20} weight="fill" className="text-[#F59E0B]" />
                    <span className="font-medium text-gray-600">{trip.traveler_rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
            </div>
            <span className="px-4 py-2 rounded-full text-sm font-semibold bg-green-100 text-green-700">
              {trip.status}
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">From</p>
                <p className="text-xl font-bold text-gray-900">
                  {trip.departure_city}, {trip.departure_country}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Departure Date</p>
                <div className="flex items-center gap-2">
                  <CalendarBlank size={20} weight="bold" className="text-[#6D28D9]" />
                  <p className="font-medium text-gray-900">
                    {new Date(trip.departure_date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">To</p>
                <p className="text-xl font-bold text-gray-900">
                  {trip.destination_city}, {trip.destination_country}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Arrival Date</p>
                <div className="flex items-center gap-2">
                  <CalendarBlank size={20} weight="bold" className="text-[#6D28D9]" />
                  <p className="font-medium text-gray-900">
                    {new Date(trip.arrival_date).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-[#F9FAFB] mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <PackageIcon size={32} weight="duotone" className="text-[#6D28D9]" />
                <div>
                  <p className="text-sm text-gray-500">Available Capacity</p>
                  <p className="text-2xl font-bold text-gray-900">{trip.available_weight} kg</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#6D28D9]/10 flex items-center justify-center">
                  <span className="text-lg font-bold text-[#6D28D9]">$</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Price per kg</p>
                  <p className="text-2xl font-bold text-[#6D28D9]">${trip.price_per_kg}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="flex-1 bg-[#6D28D9] hover:bg-[#5B21B6] text-gray-900 rounded-full py-6 text-lg font-semibold"
                  data-testid="request-delivery-button"
                >
                  Request Delivery
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Delivery</DialogTitle>
                  <DialogDescription>
                    Select a parcel and enter your price offer
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="parcel">Select Parcel</Label>
                    <Select value={selectedParcel} onValueChange={setSelectedParcel}>
                      <SelectTrigger data-testid="select-parcel">
                        <SelectValue placeholder="Choose a parcel" />
                      </SelectTrigger>
                      <SelectContent>
                        {parcels.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No parcels available
                          </SelectItem>
                        ) : (
                          parcels.map((parcel) => (
                            <SelectItem key={parcel.parcel_id} value={parcel.parcel_id}>
                              {parcel.origin_city} → {parcel.destination_city} ({parcel.weight} kg)
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="price">Your Price Offer ($)</Label>
                    <Input
                      id="price"
                      type="number"
                      placeholder="Enter amount"
                      value={requestedPrice}
                      onChange={(e) => setRequestedPrice(e.target.value)}
                      data-testid="price-input"
                    />
                  </div>
                  <Button
                    onClick={handleBookingRequest}
                    disabled={!selectedParcel || !requestedPrice || submitting}
                    className="w-full bg-[#6D28D9] hover:bg-[#5B21B6] text-gray-900"
                    data-testid="submit-request"
                  >
                    {submitting ? 'Sending...' : 'Send Request'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Button
              className="bg-white text-gray-900 border border-gray-200 hover:border-[#6D28D9]/50 rounded-full px-8 py-6 flex items-center gap-2"
              data-testid="message-traveler"
            >
              <ChatCircleDots size={20} weight="bold" />
              Message
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripDetails;
